import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ShieldCheck, Mail, Link2, Check, X, Copy, Download } from 'lucide-react';
import { useAuth } from './AuthContext.jsx';
import { API_BASE_URL } from '../utils/network.js';
import { toast } from '../utils/toast.js';
import './FollowSettings.css';

const card = { background: 'white', borderRadius: 12, border: '1px solid #e8eee9', padding: 20, marginBottom: 16 };
const inputSt = { width: '100%', boxSizing: 'border-box', padding: '10px 12px', border: '1.5px solid #dde4de', borderRadius: 8, fontSize: 14, fontFamily: 'inherit', outline: 'none' };
const btnPrimary = { padding: '10px 18px', background: '#32614d', color: 'white', border: 'none', borderRadius: 9, fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' };
const btnGhost = { padding: '10px 18px', background: 'white', color: '#555', border: '1.5px solid #dde4de', borderRadius: 9, fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' };
const btnDanger = { padding: '10px 18px', background: '#fff5f5', color: '#dc2626', border: '1.5px solid #fecaca', borderRadius: 9, fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' };

function SecuritySettings() {
  const navigate = useNavigate();
  const { user, setUser } = useAuth() || {};
  const getToken = () => localStorage.getItem('token');
  const authHeaders = () => ({ Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' });

  // ── 2FA ──
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(!!user?.twoFactorEnabled);
  const [setupStep, setSetupStep] = useState(null); // null | 'setup' | 'backupCodes'
  const [setupSecret, setSetupSecret] = useState('');
  const [setupOtpUrl, setSetupOtpUrl] = useState('');
  const [confirmCode, setConfirmCode] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [busy2fa, setBusy2fa] = useState(false);
  const [showDisableForm, setShowDisableForm] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');
  const [disableCode, setDisableCode] = useState('');

  // ── Email change ──
  const [showEmailChange, setShowEmailChange] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [emailChangePassword, setEmailChangePassword] = useState('');
  const [emailCodeSent, setEmailCodeSent] = useState(false);
  const [emailConfirmCode, setEmailConfirmCode] = useState('');
  const [busyEmail, setBusyEmail] = useState(false);

  // ── Connected accounts ──
  const [unlinkPassword, setUnlinkPassword] = useState('');
  const [showUnlinkForm, setShowUnlinkForm] = useState(false);
  const [busyUnlink, setBusyUnlink] = useState(false);

  const hasPassword = !!user?.password || user?.authProvider === 'local' || !user?.authProvider;
  const isSocial = user?.authProvider && user.authProvider !== 'local';

  const startSetup = async () => {
    try {
      setBusy2fa(true);
      const res = await fetch(`${API_BASE_URL}/user/2fa/setup`, { method: 'POST', headers: authHeaders() });
      const data = await res.json();
      if (res.ok && data.success) {
        setSetupSecret(data.secret);
        setSetupOtpUrl(data.otpauthUrl);
        setSetupStep('setup');
      } else toast(data.error || 'Failed to start setup');
    } catch { toast('Failed to start setup'); }
    finally { setBusy2fa(false); }
  };

  const confirmSetup = async () => {
    try {
      setBusy2fa(true);
      const res = await fetch(`${API_BASE_URL}/user/2fa/confirm`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ code: confirmCode }) });
      const data = await res.json();
      if (res.ok && data.success) {
        setBackupCodes(data.backupCodes || []);
        setSetupStep('backupCodes');
        setTwoFactorEnabled(true);
        setConfirmCode('');
        setUser && setUser((u) => ({ ...u, twoFactorEnabled: true }));
      } else toast(data.error || 'Invalid code');
    } catch { toast('Invalid code'); }
    finally { setBusy2fa(false); }
  };

  const disable2fa = async () => {
    try {
      setBusy2fa(true);
      const res = await fetch(`${API_BASE_URL}/user/2fa/disable`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ password: disablePassword, code: disableCode }) });
      const data = await res.json();
      if (res.ok && data.success) {
        setTwoFactorEnabled(false);
        setShowDisableForm(false);
        setDisablePassword('');
        setDisableCode('');
        setUser && setUser((u) => ({ ...u, twoFactorEnabled: false }));
        toast('Two-factor authentication disabled');
      } else toast(data.error || 'Failed to disable');
    } catch { toast('Failed to disable'); }
    finally { setBusy2fa(false); }
  };

  const downloadBackupCodes = () => {
    const blob = new Blob([`Greenwich backup codes\n\n${backupCodes.join('\n')}\n\nEach code can be used once if you lose access to your authenticator app.`], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'greenwich-backup-codes.txt';
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const requestEmailChange = async () => {
    try {
      setBusyEmail(true);
      const res = await fetch(`${API_BASE_URL}/user/email/request-change`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ newEmail, password: emailChangePassword }) });
      const data = await res.json();
      if (res.ok && data.success) { setEmailCodeSent(true); toast(data.message || 'Code sent'); }
      else toast(data.error || 'Failed to request change');
    } catch { toast('Failed to request change'); }
    finally { setBusyEmail(false); }
  };

  const confirmEmailChange = async () => {
    try {
      setBusyEmail(true);
      const res = await fetch(`${API_BASE_URL}/user/email/confirm-change`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ code: emailConfirmCode }) });
      const data = await res.json();
      if (res.ok && data.success) {
        toast('Email updated');
        setUser && setUser((u) => ({ ...u, email: data.email }));
        setShowEmailChange(false); setEmailCodeSent(false); setNewEmail(''); setEmailChangePassword(''); setEmailConfirmCode('');
      } else toast(data.error || 'Incorrect code');
    } catch { toast('Incorrect code'); }
    finally { setBusyEmail(false); }
  };

  const unlinkProvider = async () => {
    try {
      setBusyUnlink(true);
      const res = await fetch(`${API_BASE_URL}/user/unlink-provider`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ newPassword: unlinkPassword || undefined }) });
      const data = await res.json();
      if (res.ok && data.success) {
        toast('Account unlinked');
        setUser && setUser((u) => ({ ...u, authProvider: 'local' }));
        setShowUnlinkForm(false); setUnlinkPassword('');
      } else toast(data.error || 'Failed to unlink');
    } catch { toast('Failed to unlink'); }
    finally { setBusyUnlink(false); }
  };

  return (
    <div className="follow-settings-container">
      <button onClick={() => navigate('/settings')} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: '#32614d', fontWeight: 600, fontSize: 14, cursor: 'pointer', marginBottom: 16, padding: 0 }}>
        <ArrowLeft size={16} /> Back to Settings
      </button>

      <div className="settings-header">
        <h2>Security</h2>
        <p className="settings-subtitle">Two-factor authentication, email, and connected accounts.</p>
      </div>

      {/* Two-factor authentication */}
      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: '#1a3d2e', margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: 8 }}>
              <ShieldCheck size={16} /> Two-Factor Authentication
            </h3>
            <p style={{ fontSize: 13, color: '#888', margin: 0 }}>
              {twoFactorEnabled ? 'Enabled. An authenticator code is required at login.' : 'Add an extra step at login using an authenticator app.'}
            </p>
          </div>
          {!setupStep && (twoFactorEnabled ? (
            <button onClick={() => setShowDisableForm(v => !v)} style={btnDanger}>Disable</button>
          ) : (
            <button onClick={startSetup} disabled={busy2fa} style={btnPrimary}>Enable</button>
          ))}
        </div>

        {setupStep === 'setup' && (
          <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #f0f0f0' }}>
            <p style={{ fontSize: 13, color: '#555' }}>1. Add this to your authenticator app (Google Authenticator, Authy, 1Password...). Enter this key manually:</p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '8px 0 16px' }}>
              <code style={{ background: '#f4f7f5', padding: '8px 12px', borderRadius: 8, fontSize: 14, letterSpacing: 1, wordBreak: 'break-all' }}>{setupSecret}</code>
              <button onClick={() => { navigator.clipboard?.writeText(setupSecret); toast('Copied'); }} style={{ ...btnGhost, padding: 8 }}><Copy size={14} /></button>
            </div>
            <p style={{ fontSize: 13, color: '#555', margin: '0 0 8px' }}>2. Enter the 6-digit code it shows:</p>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <input value={confirmCode} onChange={e => setConfirmCode(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="123456" style={{ ...inputSt, width: 140 }} />
              <button onClick={confirmSetup} disabled={busy2fa || confirmCode.length !== 6} style={btnPrimary}>Confirm</button>
              <button onClick={() => setSetupStep(null)} style={btnGhost}>Cancel</button>
            </div>
          </div>
        )}

        {setupStep === 'backupCodes' && (
          <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #f0f0f0' }}>
            <p style={{ fontSize: 13, fontWeight: 700, color: '#1a3d2e' }}>Save your backup codes</p>
            <p style={{ fontSize: 13, color: '#888', margin: '0 0 12px' }}>Each code works once, if you ever lose access to your authenticator app. Store them somewhere safe, they won't be shown again.</p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 8, background: '#f4f7f5', borderRadius: 8, padding: 14, marginBottom: 12 }}>
              {backupCodes.map(c => <code key={c} style={{ fontSize: 13 }}>{c}</code>)}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={downloadBackupCodes} style={btnPrimary}><Download size={13} style={{ verticalAlign: -2, marginRight: 6 }} />Download codes</button>
              <button onClick={() => setSetupStep(null)} style={btnGhost}>Done</button>
            </div>
          </div>
        )}

        {showDisableForm && (
          <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #f0f0f0' }}>
            <p style={{ fontSize: 13, color: '#555', margin: '0 0 8px' }}>Enter your password and a current authenticator (or backup) code:</p>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <input type="password" value={disablePassword} onChange={e => setDisablePassword(e.target.value)} placeholder="Password" style={{ ...inputSt, width: 180 }} />
              <input value={disableCode} onChange={e => setDisableCode(e.target.value)} placeholder="Code" style={{ ...inputSt, width: 120 }} />
              <button onClick={disable2fa} disabled={busy2fa} style={btnDanger}>Disable 2FA</button>
            </div>
          </div>
        )}
      </div>

      {/* Email */}
      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: '#1a3d2e', margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: 8 }}>
              <Mail size={16} /> Email Address
            </h3>
            <p style={{ fontSize: 13, color: '#888', margin: 0 }}>{user?.email}</p>
          </div>
          {!showEmailChange && <button onClick={() => setShowEmailChange(true)} style={btnGhost}>Change</button>}
        </div>

        {showEmailChange && !emailCodeSent && (
          <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #f0f0f0', display: 'flex', flexDirection: 'column', gap: 8 }}>
            <input value={newEmail} onChange={e => setNewEmail(e.target.value)} placeholder="New email address" style={inputSt} />
            {hasPassword && <input type="password" value={emailChangePassword} onChange={e => setEmailChangePassword(e.target.value)} placeholder="Current password" style={inputSt} />}
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={requestEmailChange} disabled={busyEmail || !newEmail} style={btnPrimary}>Send confirmation code</button>
              <button onClick={() => setShowEmailChange(false)} style={btnGhost}>Cancel</button>
            </div>
          </div>
        )}
        {showEmailChange && emailCodeSent && (
          <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #f0f0f0', display: 'flex', flexDirection: 'column', gap: 8 }}>
            <p style={{ fontSize: 13, color: '#555', margin: 0 }}>Enter the code sent to {newEmail}:</p>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <input value={emailConfirmCode} onChange={e => setEmailConfirmCode(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="123456" style={{ ...inputSt, width: 140 }} />
              <button onClick={confirmEmailChange} disabled={busyEmail || emailConfirmCode.length !== 6} style={btnPrimary}>Confirm</button>
              <button onClick={() => { setShowEmailChange(false); setEmailCodeSent(false); }} style={btnGhost}>Cancel</button>
            </div>
          </div>
        )}
      </div>

      {/* Connected accounts */}
      {isSocial && (
        <div style={card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <div>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: '#1a3d2e', margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: 8 }}>
                <Link2 size={16} /> Connected Accounts
              </h3>
              <p style={{ fontSize: 13, color: '#888', margin: 0 }}>Signed in with {user.authProvider === 'google' ? 'Google' : 'Facebook'}.</p>
            </div>
            {!showUnlinkForm && <button onClick={() => setShowUnlinkForm(true)} style={btnDanger}>Unlink</button>}
          </div>
          {showUnlinkForm && (
            <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #f0f0f0', display: 'flex', flexDirection: 'column', gap: 8 }}>
              {!hasPassword && (
                <>
                  <p style={{ fontSize: 13, color: '#555', margin: 0 }}>Set a password so you can still sign in after unlinking:</p>
                  <input type="password" value={unlinkPassword} onChange={e => setUnlinkPassword(e.target.value)} placeholder="New password (min 8 characters)" style={inputSt} />
                </>
              )}
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={unlinkProvider} disabled={busyUnlink || (!hasPassword && unlinkPassword.length < 8)} style={btnDanger}>Confirm unlink</button>
                <button onClick={() => setShowUnlinkForm(false)} style={btnGhost}>Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default SecuritySettings;
